import { useEffect, useMemo, useState } from 'react';
import { Banknote, TrendingDown, Star, Trophy, Info, Lock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { useViewport } from '@/hooks/useViewport';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { AnimatedCounter } from '@/components/ui/AnimatedCounter';
import { formatEuro } from '@/lib/format';
import { isAtCap } from '@/data/leveling';
import { HeldPersonsModal } from './HeldPersonsModal';

interface TopBarFloatingProps {
  onOpenLevelUp: () => void;
}

/**
 * Floating glass card, top-right.
 *  - Desktop: full row with Geld | Kosten | Level | Punkte and labels.
 *  - Tablet: same, no label rows.
 *  - Mobile: compact icon + value (no labels) + tap-to-open detail sheet.
 */
export function TopBarFloating({ onOpenLevelUp }: TopBarFloatingProps) {
  const credits = useGameStore((s) => s.credits);
  const dailyUpkeep = useGameStore((s) => s.dailyUpkeep);
  const level = useGameStore((s) => s.level);
  const points = useGameStore((s) => s.points);
  const stations = useGameStore((s) => s.stations);
  const atCap = isAtCap(level);
  const viewport = useViewport();
  const [heldOpen, setHeldOpen] = useState(false);

  // Phase 10.5.1 — Aggregate held persons across all police stations.
  const totalHeld = useMemo(() => {
    let n = 0;
    for (const s of stations) n += s.heldPersons?.length ?? 0;
    return n;
  }, [stations]);

  const [flash, setFlash] = useState<'up' | 'down' | null>(null);
  const [prev, setPrev] = useState(credits);
  useEffect(() => {
    if (credits === prev) return;
    setFlash(credits > prev ? 'up' : 'down');
    setPrev(credits);
    const t = setTimeout(() => setFlash(null), 600);
    return () => clearTimeout(t);
  }, [credits, prev]);

  const [detailSheet, setDetailSheet] = useState(false);

  // Phase 8.6.3 — Werte sind grundsätzlich weiß (fg-primary). Nur ein kurzer Flash
  // bei Veränderung markiert Auf/Ab; die Ruhefarbe bleibt einheitlich weiß.
  const flashClass =
    flash === 'up'
      ? 'text-status-available'
      : flash === 'down'
        ? 'text-accent-emergency'
        : 'text-fg-primary';

  // Mobile: compact card (~48 px high), tappable
  if (viewport === 'mobile') {
    return (
      <>
        <button
          type="button"
          onClick={() => setDetailSheet(true)}
          aria-label="Spielinfo öffnen"
          className="pointer-events-auto fixed left-1/2 top-3 z-30 flex h-12 -translate-x-1/2 items-center gap-2.5 overflow-hidden rounded-full border border-white/5 px-3 shadow-xl backdrop-blur-md"
          style={{ background: 'rgba(20, 24, 36, 0.9)' }}
        >
          <Banknote className="h-3.5 w-3.5 text-status-available" aria-hidden />
          <span
            className={['font-display text-sm font-bold tabular-nums', flashClass].join(' ')}
          >
            <AnimatedCounter value={credits} format={(n) => formatEuro(n)} />
          </span>
          <span className="h-3 w-px bg-white/10" aria-hidden />
          <Star className="h-3.5 w-3.5 text-amber-400" aria-hidden />
          <span className="font-display text-sm font-bold tabular-nums">{level}</span>
          <Info className="h-3 w-3 text-fg-muted" aria-hidden />
        </button>
        <BottomSheet
          open={detailSheet}
          onClose={() => setDetailSheet(false)}
          title="Spielinfo"
        >
          <div className="space-y-3 px-5 pb-6 pt-2">
            <DetailRow icon={Banknote} iconClass="text-status-available" label="Dein Geld">
              <AnimatedCounter value={credits} format={(n) => formatEuro(n)} />
            </DetailRow>
            <DetailRow
              icon={TrendingDown}
              iconClass="text-accent-emergency"
              label="Tägliche Kosten"
            >
              -{formatEuro(dailyUpkeep)}
            </DetailRow>
            <DetailRow icon={Star} iconClass="text-amber-400" label="Level">
              <button
                type="button"
                onClick={() => {
                  setDetailSheet(false);
                  onOpenLevelUp();
                }}
                disabled={atCap}
                className={[
                  'rounded-full px-3 py-1 text-xs font-semibold',
                  atCap
                    ? 'bg-bg-elevated text-fg-muted'
                    : 'bg-accent-dispatch text-white',
                ].join(' ')}
              >
                Level {level} {atCap ? '(Max)' : '— erhöhen'}
              </button>
            </DetailRow>
            <DetailRow icon={Trophy} iconClass="text-amber-400" label="Punkte">
              {points.toLocaleString('de-DE')}
            </DetailRow>
          </div>
        </BottomSheet>
      </>
    );
  }

  // Tablet/Desktop: full row
  return (
    <div
      className={[
        'pointer-events-auto fixed right-4 top-4 z-30 flex items-stretch overflow-hidden',
        'rounded-2xl border border-white/5 backdrop-blur-md shadow-xl',
      ].join(' ')}
      style={{ background: 'rgba(20, 24, 36, 0.9)' }}
    >
      <Compartment
        icon={Banknote}
        iconClass="text-status-available"
        label="Dein Geld"
        valueClass={flashClass}
      >
        <AnimatedCounter value={credits} format={(n) => formatEuro(n)} />
      </Compartment>
      <Divider />
      <Compartment
        icon={TrendingDown}
        iconClass="text-accent-emergency"
        label="Tägl. Kosten"
        valueClass="text-fg-primary"
      >
        -{formatEuro(dailyUpkeep)}
      </Compartment>
      <Divider />
      <Compartment
        icon={Star}
        iconClass="text-amber-400"
        label="Level"
        valueClass="text-fg-primary"
        onClick={onOpenLevelUp}
        title={atCap ? 'Höchstes Level erreicht' : 'Level erhöhen'}
      >
        {String(level)}
      </Compartment>
      <Divider />
      <Compartment
        icon={Trophy}
        iconClass="text-amber-400"
        label="Punkte"
        valueClass="text-fg-primary"
      >
        {points.toLocaleString('de-DE')}
      </Compartment>
      {totalHeld > 0 && (
        <>
          <Divider />
          <Compartment
            icon={Lock}
            iconClass="text-blue-400"
            label="Festgenommene"
            valueClass="text-fg-primary"
            onClick={() => setHeldOpen(true)}
            title="Festgenommene verwalten"
          >
            {String(totalHeld)}
          </Compartment>
        </>
      )}
      <HeldPersonsModal open={heldOpen} onClose={() => setHeldOpen(false)} />
    </div>
  );
}

interface CompartmentProps {
  icon: LucideIcon;
  iconClass: string;
  label: string;
  valueClass: string;
  onClick?: () => void;
  title?: string;
  children: React.ReactNode;
}

function Compartment({
  icon: Icon,
  iconClass,
  label,
  valueClass,
  onClick,
  title,
  children,
}: CompartmentProps) {
  const Inner = (
    <div className="flex items-center gap-2 px-3 py-1.5">
      <Icon className={['h-4 w-4 flex-shrink-0', iconClass].join(' ')} aria-hidden />
      <div className="flex flex-col leading-none">
        <span className="hidden text-[9px] uppercase tracking-wider text-fg-muted md:inline">
          {label}
        </span>
        <span className={['font-display text-base font-bold tabular-nums', valueClass].join(' ')}>
          {children}
        </span>
      </div>
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        className="transition hover:bg-white/5 focus:outline-none focus-visible:bg-white/5"
      >
        {Inner}
      </button>
    );
  }
  return <div title={title}>{Inner}</div>;
}

function Divider() {
  return <span aria-hidden className="my-2 w-px bg-white/10" />;
}

function DetailRow({
  icon: Icon,
  iconClass,
  label,
  children,
}: {
  icon: LucideIcon;
  iconClass: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-bg-elevated/30 p-3">
      <span className="flex items-center gap-2.5">
        <Icon className={['h-4 w-4', iconClass].join(' ')} aria-hidden />
        <span className="text-xs text-fg-muted">{label}</span>
      </span>
      <span className="font-display text-sm font-bold tabular-nums text-fg-primary">
        {children}
      </span>
    </div>
  );
}
